import React, { useState, useEffect } from 'react';
import { updateSEOMetadata } from './utils/seo';
import {
  fetchNewsApi,
  fetchEventsApi,
  fetchBannersApi,
  fetchSettingsApi,
  fetchActivitiesApi,
  fetchCultureApi,
  fetchMembersApi,
  checkAdminAuthApi,
  fetchGalleryApi,
  fetchDocumentsApi,
  fetchDonationsApi,
  fetchBoardApi,
  fetchAdsApi,
  fetchEventTicketsApi,
  fetchTicketPurchasesApi,
} from './services/api';
import {
  LanguageCode,
  HeroSlide,
  NewsItem,
  EventItem,
  ActivityItem,
  CultureItem,
  BoardMember,
  MemberUser,
  DonationRecord,
  MediaGalleryItem,
  DocumentItem,
  SiteSettings,
  AdminAccount,
  AdvertisementItem,
  EventTicket,
  UserTicketPurchase,
} from './types';
import {
  initialHeroSlides,
  initialNews,
  initialEvents,
  initialActivities,
  initialCultureItems,
  initialBoardMembers,
  initialMembers,
  initialDonations,
  initialGallery,
  initialDocuments,
  initialSiteSettings,
  initialAdminAccounts,
  initialAdvertisements,
  initialEventTickets,
  initialUserTicketPurchases,
} from './data/mockData';
import { translations } from './i18n/translations';

// Components
import { Header } from './components/Header';
import { BottomMobileNav } from './components/BottomMobileNav';
import { RightDrawer } from './components/RightDrawer';
import { SearchModal } from './components/SearchModal';
import { NavigationHistoryBar } from './components/NavigationHistoryBar';

// Views
import { HomeView } from './views/HomeView';
import { AboutView } from './views/AboutView';
import { ActivitiesView } from './views/ActivitiesView';
import { NewsView } from './views/NewsView';
import { EventsView } from './views/EventsView';
import { CultureView } from './views/CultureView';
import { DonateView } from './views/DonateView';
import { GalleryView } from './views/GalleryView';
import { DocumentsView } from './views/DocumentsView';
import { ContactView } from './views/ContactView';
import { LiveStreamView } from './views/LiveStreamView';
import { MembershipView } from './views/MembershipView';
import { AdsView } from './views/AdsView';
import { TicketStoreView } from './views/TicketStoreView';
import { AuthModal } from './views/AuthModal';
import { AdminAuthModal } from './views/AdminAuthModal';
import { MemberPanel } from './views/MemberPanel';
import { AdminPanel } from './views/AdminPanel';

import { Heart, Globe, Phone, Mail, MapPin, ExternalLink, ShieldCheck, CheckCircle2, X } from 'lucide-react';

// Router URL Slugs Mapping
const tabToSlug: Record<string, string> = {
  home: '/',
  about: '/kurumsal',
  activities: '/faaliyetler',
  news: '/haberler',
  events: '/etkinlikler',
  culture: '/kurt-kulturu',
  ads: '/reklamlar',
  membership: '/uyelik',
  donate: '/bagis',
  gallery: '/galeri',
  documents: '/belgeler',
  contact: '/iletisim',
  live: '/canli-yayin',
  member: '/uye-portali',
  admin: '/yonetim',
  tickets: '/bilet-satis',
};

const slugToTab: Record<string, string> = {
  '': 'home',
  '/': 'home',
  '/home': 'home',
  '/anasayfa': 'home',

  '/about': 'about',
  '/kurumsal': 'about',
  '/hakkimizda': 'about',

  '/activities': 'activities',
  '/faaliyetler': 'activities',

  '/news': 'news',
  '/haberler': 'news',
  '/duyurular': 'news',

  '/events': 'events',
  '/etkinlikler': 'events',

  '/culture': 'culture',
  '/kurt-kulturu': 'culture',
  '/kultur': 'culture',

  '/ads': 'ads',
  '/reklamlar': 'ads',
  '/reklam': 'ads',

  '/membership': 'membership',
  '/uyelik': 'membership',

  '/donate': 'donate',
  '/bagis': 'donate',
  '/bagis-yap': 'donate',

  '/gallery': 'gallery',
  '/galeri': 'gallery',

  '/documents': 'documents',
  '/belgeler': 'documents',
  '/tuzuk': 'documents',

  '/contact': 'contact',
  '/iletisim': 'contact',

  '/live': 'live',
  '/canli-yayin': 'live',
  '/canli': 'live',

  '/member': 'member',
  '/member-panel': 'member',
  '/uye-portali': 'member',
  '/uye-paneli': 'member',

  '/admin': 'admin',
  '/yonetim': 'admin',
  '/yonetim-paneli': 'admin',

  '/tickets': 'tickets',
  '/bilet': 'tickets',
  '/bilet-satis': 'tickets',
};

const getTabFromUrl = (): string => {
  if (typeof window === 'undefined') return 'home';

  // 1. Check hash first (e.g. #/kurumsal or #about)
  const hashRaw = window.location.hash.replace(/^#\/?/, '').toLowerCase().trim();
  if (hashRaw) {
    if (slugToTab['/' + hashRaw]) return slugToTab['/' + hashRaw];
    if (slugToTab[hashRaw]) return slugToTab[hashRaw];
  }

  // 2. Check pathname (e.g. /kurumsal or /bagis)
  const pathname = window.location.pathname.toLowerCase().replace(/\/$/, '') || '/';
  if (slugToTab[pathname]) return slugToTab[pathname];

  return 'home';
};

export default function App() {
  const getBrowserLanguage = (): LanguageCode => {
    if (typeof window !== 'undefined' && navigator && navigator.language) {
      const lang = navigator.language.toLowerCase();
      if (lang.startsWith('en')) return 'EN';
      if (lang.startsWith('tr')) return 'TR';
    }
    return 'TR';
  };

  const [currentLang, setCurrentLang] = useState<LanguageCode>(getBrowserLanguage);
  const [currentTab, setCurrentTab] = useState<string>(getTabFromUrl);
  const [tabHistory, setTabHistory] = useState<string[]>([getTabFromUrl()]);
  const [historyIndex, setHistoryIndex] = useState<number>(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  // Sync URL changes (popstate & hashchange) with React State
  useEffect(() => {
    const handleUrlChange = () => {
      const newTab = getTabFromUrl();
      setCurrentTab(newTab);
    };

    window.addEventListener('popstate', handleUrlChange);
    window.addEventListener('hashchange', handleUrlChange);

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      window.removeEventListener('hashchange', handleUrlChange);
    };
  }, []);

  // Global Toast Notification System State
  const [toast, setToast] = useState<{
    id: number;
    message: string;
    type?: 'success' | 'info' | 'warning';
    title?: string;
  } | null>(null);

  const showToast = (message: string, type: 'success' | 'info' | 'warning' = 'success', title?: string) => {
    setToast({ id: Date.now(), message, type, title });
  };

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => {
      setToast(null);
    }, 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  // User State (null if logged out, MemberUser if logged in)
  const [currentUser, setCurrentUser] = useState<MemberUser | null>(null);

  // Admin State & High Security Authentication
  const [adminAccounts, setAdminAccounts] = useState<AdminAccount[]>(initialAdminAccounts);
  const [currentAdminUser, setCurrentAdminUser] = useState<AdminAccount | null>(null);
  const [isAdminAuthModalOpen, setIsAdminAuthModalOpen] = useState(false);

  // Dynamic Content Data States (Allows full CRUD in Admin Panel)
  const [heroSlides, setHeroSlides] = useState<HeroSlide[]>(initialHeroSlides);
  const [news, setNews] = useState<NewsItem[]>(initialNews);
  const [events, setEvents] = useState<EventItem[]>(initialEvents);
  const [activities, setActivities] = useState<ActivityItem[]>(initialActivities);
  const [cultureItems, setCultureItems] = useState<CultureItem[]>(initialCultureItems);
  const [boardMembers, setBoardMembers] = useState<BoardMember[]>(initialBoardMembers);
  const [members, setMembers] = useState<MemberUser[]>(initialMembers);
  const [donations, setDonations] = useState<DonationRecord[]>(initialDonations);
  const [gallery, setGallery] = useState<MediaGalleryItem[]>(initialGallery);
  const [documents, setDocuments] = useState<DocumentItem[]>(initialDocuments);
  const [settings, setSettings] = useState<SiteSettings>(initialSiteSettings);
  const [ads, setAds] = useState<AdvertisementItem[]>(initialAdvertisements);
  const [eventTickets, setEventTickets] = useState<EventTicket[]>(initialEventTickets);
  const [userPurchases, setUserPurchases] = useState<UserTicketPurchase[]>(initialUserTicketPurchases);

  // Load dynamic data from Central Server Database API
  useEffect(() => {
    let isMounted = true;

    async function loadApiData() {
      try {
        const [
          newsData,
          eventsData,
          bannersData,
          settingsData,
          activitiesData,
          cultureData,
          membersData,
          authCheck,
          galleryData,
          documentsData,
          donationsData,
          boardData,
          adsData,
          ticketsData,
          purchasesData,
        ] = await Promise.allSettled([
          fetchNewsApi(),
          fetchEventsApi(),
          fetchBannersApi(),
          fetchSettingsApi(),
          fetchActivitiesApi(),
          fetchCultureApi(),
          fetchMembersApi(),
          checkAdminAuthApi(),
          fetchGalleryApi(),
          fetchDocumentsApi(),
          fetchDonationsApi(),
          fetchBoardApi(),
          fetchAdsApi(),
          fetchEventTicketsApi(),
          fetchTicketPurchasesApi(),
        ]);

        if (!isMounted) return;

        if (newsData.status === 'fulfilled' && Array.isArray(newsData.value)) {
          setNews(newsData.value);
        }
        if (eventsData.status === 'fulfilled' && Array.isArray(eventsData.value)) {
          setEvents(eventsData.value);
        }
        if (bannersData.status === 'fulfilled' && Array.isArray(bannersData.value)) {
          setHeroSlides(bannersData.value);
        }
        if (settingsData.status === 'fulfilled' && settingsData.value && settingsData.value.siteName) {
          setSettings(settingsData.value);
        }
        if (activitiesData.status === 'fulfilled' && Array.isArray(activitiesData.value)) {
          setActivities(activitiesData.value);
        }
        if (cultureData.status === 'fulfilled' && Array.isArray(cultureData.value)) {
          setCultureItems(cultureData.value);
        }
        if (membersData.status === 'fulfilled' && Array.isArray(membersData.value)) {
          setMembers(membersData.value);
        }
        if (authCheck.status === 'fulfilled' && authCheck.value) {
          setCurrentAdminUser(authCheck.value);
        }
        if (galleryData.status === 'fulfilled' && Array.isArray(galleryData.value)) {
          setGallery(galleryData.value);
        }
        if (documentsData.status === 'fulfilled' && Array.isArray(documentsData.value)) {
          setDocuments(documentsData.value);
        }
        if (donationsData.status === 'fulfilled' && Array.isArray(donationsData.value)) {
          setDonations(donationsData.value);
        }
        if (boardData.status === 'fulfilled' && Array.isArray(boardData.value)) {
          setBoardMembers(boardData.value);
        }
        if (adsData.status === 'fulfilled' && Array.isArray(adsData.value)) {
          setAds(adsData.value);
        }
        if (ticketsData.status === 'fulfilled' && Array.isArray(ticketsData.value)) {
          setEventTickets(ticketsData.value);
        }
        if (purchasesData.status === 'fulfilled' && Array.isArray(purchasesData.value)) {
          setUserPurchases(purchasesData.value);
        }
      } catch (err) {
        console.error('API Sync Error:', err);
      }
    }

    loadApiData();

    // Poll server every 10 seconds for multi-device live synchronization
    const syncInterval = setInterval(() => {
      loadApiData();
    }, 10000);

    return () => {
      isMounted = false;
      clearInterval(syncInterval);
    };
  }, []);

  // Selected item state for direct linking from Search / Home
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);

  const t = translations[currentLang];

  // Dynamic SEO Metadata Tag Injection Effect
  useEffect(() => {
    const siteName = settings.siteName || 'Kürt Kültürü ve Dayanışma Derneği';
    let title = siteName;
    let description = 'Kürt kültürünü, dilini ve geleneklerini korumak, yaşatmak ve gelecek kuşaklara aktarmak amacıyla çalışan sivil toplum kuruluşu.';
    let image = 'https://images.unsplash.com/photo-1541872703-74c5e44368f9?auto=format&fit=crop&q=80&w=1200';
    let type = 'website';

    const getLocalized = (field: Record<LanguageCode, string> | undefined) => {
      if (!field) return '';
      return field[currentLang] || field['TR'] || field['EN'] || '';
    };

    if (currentTab === 'home') {
      title = `${siteName} | ${t.home || 'Ana Sayfa'}`;
      description = `${siteName} - ${t.kurdishCulture || 'Kürt Kültürü ve Dayanışma Derneği'}. ${settings.address || ''}`;
      if (heroSlides && heroSlides.length > 0) {
        image = heroSlides[0].image || image;
      }
    } else if (currentTab === 'news') {
      if (selectedNews) {
        const newsTitle = getLocalized(selectedNews.title);
        const newsSummary = getLocalized(selectedNews.summary) || getLocalized(selectedNews.content);
        title = `${newsTitle} | ${siteName}`;
        description = newsSummary ? newsSummary.slice(0, 160) : description;
        image = selectedNews.image || image;
        type = 'article';
      } else {
        title = `${t.news || 'Haberler & Duyurular'} | ${siteName}`;
        description = `${t.latestNews || 'En güncel haberler ve basın açıklamaları'} - ${siteName}`;
        if (news && news.length > 0) {
          image = news[0].image || image;
        }
      }
    } else if (currentTab === 'events') {
      if (selectedEvent) {
        const eventTitle = getLocalized(selectedEvent.title);
        const eventDesc = getLocalized(selectedEvent.description);
        title = `${eventTitle} | ${siteName}`;
        description = eventDesc ? eventDesc.slice(0, 160) : description;
        image = selectedEvent.image || image;
        type = 'event';
      } else {
        title = `${t.events || 'Etkinlikler'} | ${siteName}`;
        description = `${t.upcomingEvents || 'Yaklaşan etkinlikler, konserler ve buluşmalar'} - ${siteName}`;
        if (events && events.length > 0) {
          image = events[0].image || image;
        }
      }
    } else if (currentTab === 'about') {
      title = `${t.aboutUs || 'Hakkımızda'} | ${siteName}`;
      description = `${siteName} - Derneğimizin vizyonu, misyonu ve yönetim kurulu üyeleri.`;
    } else if (currentTab === 'activities') {
      title = `${t.activities || 'Faaliyetler'} | ${siteName}`;
      description = `${siteName} - Kültürel, eğitsel ve sosyal faaliyetler.`;
    } else if (currentTab === 'culture') {
      title = `${t.culture || 'Kürt Kültürü'} | ${siteName}`;
      description = `Kürt dili, tarihi, dengbêjlik geleneği, edebiyatı ve mutfağı hakkında bilgiler.`;
    } else if (currentTab === 'membership') {
      title = `${t.membership || 'Üyelik'} | ${siteName}`;
      description = `${siteName} üyelik başvuru ve katılım süreci.`;
    } else if (currentTab === 'donate') {
      title = `${t.donate || 'Bağış Yap'} | ${siteName}`;
      description = `${siteName} kültür ve dil projelerini desteklemek için bağış yapın.`;
    } else if (currentTab === 'gallery') {
      title = `${t.gallery || 'Galeri'} | ${siteName}`;
      description = `${siteName} fotoğraf ve video galerisi.`;
    } else if (currentTab === 'documents') {
      title = `${t.documents || 'Belgeler'} | ${siteName}`;
      description = `${siteName} tüzüğü, faaliyet ve mali raporları.`;
    } else if (currentTab === 'contact') {
      title = `${t.contact || 'İletişim'} | ${siteName}`;
      description = `${siteName} iletişim bilgileri, adres, e-posta ve konum.`;
    } else if (currentTab === 'live') {
      title = `${t.liveStream || 'Canlı Yayın'} | ${siteName}`;
      description = `${settings.liveStream?.title || 'Canlı Yayın'} - ${siteName}`;
    } else if (currentTab === 'member' || currentTab === 'member-panel') {
      title = `${t.memberPanel || 'Üye Paneli'} | ${siteName}`;
      description = `Üye profil ve aidat takip sistemi.`;
    } else if (currentTab === 'admin') {
      title = `${t.adminPanel || 'Yönetim Paneli'} | ${siteName}`;
      description = `${siteName} yönetim ve içerik paneli.`;
    }

    updateSEOMetadata({
      title,
      description,
      image,
      type,
      lang: currentLang,
      siteName,
    });
  }, [currentTab, currentLang, selectedNews, selectedEvent, settings, news, events, heroSlides, t]);

  const handleNavClick = (tab: string, pushState = true) => {
    if (currentAdminUser) {
      setCurrentTab('admin');
      if (tab === 'home' || tab === 'admin' || tab === 'dashboard') {
        window.dispatchEvent(new CustomEvent('reset-admin-dashboard'));
      } else {
        window.dispatchEvent(new CustomEvent('change-admin-tab', { detail: tab }));
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (currentUser) {
      setCurrentTab('member');
      if (tab === 'home' || tab === 'member' || tab === 'member-panel' || tab === 'dashboard') {
        window.dispatchEvent(new CustomEvent('reset-member-dashboard'));
      } else {
        window.dispatchEvent(new CustomEvent('change-member-tab', { detail: tab }));
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (tab === 'admin') {
      if (!currentAdminUser) {
        setIsAdminAuthModalOpen(true);
        return;
      }
    }
    if (tab === 'member' || tab === 'member-panel') {
      if (!currentUser) {
        setIsAuthModalOpen(true);
        return;
      }
      tab = 'member';
    }

    if (tab !== tabHistory[historyIndex]) {
      const newHistory = [...tabHistory.slice(0, historyIndex + 1), tab];
      setTabHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
    setCurrentTab(tab);

    if (pushState && typeof window !== 'undefined') {
      const slug = tabToSlug[tab] || '/';
      if (window.location.pathname !== slug) {
        try {
          window.history.pushState({ tab }, '', slug);
        } catch {
          window.location.hash = slug.replace(/^\//, '');
        }
      }
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGoBack = () => {
    if (historyIndex > 0) {
      const prevIndex = historyIndex - 1;
      setHistoryIndex(prevIndex);
      setCurrentTab(tabHistory[prevIndex]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleGoForward = () => {
    if (historyIndex < tabHistory.length - 1) {
      const nextIndex = historyIndex + 1;
      setHistoryIndex(nextIndex);
      setCurrentTab(tabHistory[nextIndex]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleGoHome = () => {
    handleNavClick('home');
  };

  const handleSelectNews = (newsItem: NewsItem) => {
    setSelectedNews(newsItem);
    handleNavClick('news');
  };

  const handleSelectEvent = (eventItem: EventItem) => {
    setSelectedEvent(eventItem);
    handleNavClick('events');
  };

  const handleLoginSuccess = (emailVal?: string, nameVal?: string) => {
    const foundUser = members.find(
      (m) => m.email.toLowerCase() === (emailVal || '').toLowerCase()
    ) || {
      id: 'mem-' + Date.now(),
      fullName: nameVal || emailVal?.split('@')[0] || 'Üye Kullanıcı',
      email: emailVal || 'uye@kurdishculture.org',
      membershipNumber: 'KKDD-2026-99',
      status: 'active' as const,
      duesPaidStatus: 'paid' as const,
      joinedDate: new Date().toISOString().split('T')[0],
      phone: '+33 6 00 00 00 00',
    };
    setCurrentUser(foundUser);
    setCurrentTab('member');
  };

  return (
    <div className="min-h-screen bg-[#132238] text-[#F3F4F6] font-sans antialiased selection:bg-[#38BDF8]/30 selection:text-white flex flex-col justify-between relative">
      {/* GLOBAL TOAST NOTIFICATION POPUP */}
      {toast && (
        <div className="fixed top-20 right-4 sm:right-8 z-50 animate-in slide-in-from-top-4 fade-in duration-300 pointer-events-auto">
          <div className="flex items-center gap-3.5 px-4 py-3.5 rounded-2xl bg-[#0B1426] text-white border-2 border-[#38BDF8] shadow-[0_10px_35px_rgba(0,0,0,0.6)] backdrop-blur-md max-w-md">
            <div className="w-9 h-9 rounded-xl bg-[#38BDF8]/20 border border-[#38BDF8] text-[#38BDF8] flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-5 h-5 text-[#38BDF8]" />
            </div>
            <div className="flex-1 text-xs space-y-0.5">
              {toast.title && (
                <p className="font-extrabold text-[#38BDF8] font-heading uppercase tracking-wide text-[11px]">
                  {toast.title}
                </p>
              )}
              <p className="font-medium text-gray-200 leading-snug">{toast.message}</p>
            </div>
            <button
              onClick={() => setToast(null)}
              className="p-1 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* 1. TOP HEADER */}
      <Header
        currentLang={currentLang}
        onLanguageChange={setCurrentLang}
        currentTab={currentTab}
        activeTab={currentTab}
        onNavClick={handleNavClick}
        onOpenDrawer={() => setIsDrawerOpen(true)}
        onToggleDrawer={() => setIsDrawerOpen(true)}
        onOpenSearch={() => setIsSearchOpen(true)}
        isAdminMode={currentTab === 'admin'}
        onToggleAdmin={() => handleNavClick(currentTab === 'admin' ? 'home' : 'admin')}
        isLoggedIn={!!currentUser}
        onOpenAuth={() => setIsAuthModalOpen(true)}
        onOpenAuthModal={() => setIsAuthModalOpen(true)}
        settings={settings}
      />

      {/* 2. MAIN CONTENT AREA (Koyu mavinin açık tonu anasayfa arka planı) */}
      <div className="flex-1 w-full bg-[#132238] border-y border-[#38BDF8]/10 flex flex-col items-center">
        <main className={`flex-1 w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 ${
          currentTab === 'home'
            ? 'pt-[80px] sm:pt-[90px] pb-[80px] sm:pb-[90px] min-h-[calc(100vh-80px)] flex flex-col justify-center items-center my-auto overflow-hidden'
            : 'pt-[136px] sm:pt-[148px] pb-28 overflow-x-hidden'
        }`}>
        <NavigationHistoryBar
          currentTab={currentTab}
          canGoBack={historyIndex > 0}
          canGoForward={historyIndex < tabHistory.length - 1}
          onGoBack={handleGoBack}
          onGoForward={handleGoForward}
          onGoHome={() => {
            if (currentTab === 'admin') {
              window.dispatchEvent(new CustomEvent('reset-admin-dashboard'));
            } else {
              handleGoHome();
            }
          }}
          currentLang={currentLang}
          onLogout={async () => {
            if (currentTab === 'admin') {
              try {
                const { logoutAdminApi } = await import('./services/api');
                await logoutAdminApi();
              } catch {}
              setCurrentAdminUser(null);
              setCurrentTab('home');
            } else if (currentUser) {
              setCurrentUser(null);
              setCurrentTab('home');
            }
          }}
        />
        {currentTab === 'home' && (
          <HomeView
            currentLang={currentLang}
            heroSlides={heroSlides}
            events={events}
            news={news}
            activities={activities}
            cultureItems={cultureItems}
            gallery={gallery}
            settings={settings}
            onNavClick={handleNavClick}
            onOpenNewsDetail={handleSelectNews}
            onOpenEventDetail={handleSelectEvent}
          />
        )}

        {currentTab === 'about' && (
          <AboutView
            currentLang={currentLang}
            boardMembers={boardMembers}
            onNavClick={handleNavClick}
          />
        )}

        {currentTab === 'activities' && (
          <ActivitiesView
            currentLang={currentLang}
            activities={activities}
            onNavClick={handleNavClick}
          />
        )}

        {currentTab === 'news' && (
          <NewsView
            currentLang={currentLang}
            news={news}
            selectedNewsItem={selectedNews}
            onClearSelectedNews={() => setSelectedNews(null)}
            onSelectNewsItem={setSelectedNews}
          />
        )}

        {currentTab === 'events' && (
          <EventsView
            currentLang={currentLang}
            events={events}
            selectedEventItem={selectedEvent}
            onClearSelectedEvent={() => setSelectedEvent(null)}
            onSelectEventItem={setSelectedEvent}
          />
        )}

        {currentTab === 'culture' && (
          <CultureView
            currentLang={currentLang}
            cultureItems={cultureItems}
          />
        )}

        {currentTab === 'donate' && (
          <DonateView
            currentLang={currentLang}
            currentUser={currentUser}
            onOpenAuthModal={() => setIsAuthModalOpen(true)}
            settings={settings}
            onShowToast={showToast}
            onDonationSuccess={(newDonation) => {
              setDonations((prev) => [newDonation, ...prev]);
            }}
          />
        )}

        {currentTab === 'gallery' && (
          <GalleryView
            currentLang={currentLang}
            gallery={gallery}
          />
        )}

        {currentTab === 'documents' && (
          <DocumentsView
            currentLang={currentLang}
            documents={documents}
          />
        )}

        {currentTab === 'contact' && (
          <ContactView
            currentLang={currentLang}
            settings={settings}
          />
        )}

        {currentTab === 'live' && (
          <LiveStreamView
            currentLang={currentLang}
            settings={settings}
            isLoggedIn={!!currentUser}
            currentUser={currentUser}
            onOpenAuthModal={() => setIsAuthModalOpen(true)}
          />
        )}

        {currentTab === 'membership' && (
          <MembershipView
            currentLang={currentLang}
            onOpenAuthModal={() => setIsAuthModalOpen(true)}
            onNavClick={handleNavClick}
          />
        )}

        {currentTab === 'ads' && (
          <AdsView
            currentLang={currentLang}
            ads={ads}
            settings={settings}
            onNavClick={handleNavClick}
          />
        )}

        {currentTab === 'tickets' && (
          <TicketStoreView
            currentLang={currentLang}
            currentUser={currentUser}
            eventTickets={eventTickets}
            userPurchases={userPurchases}
            onPurchaseComplete={(newPurchase) => {
              setUserPurchases((prev) => [newPurchase, ...prev]);
            }}
            onOpenAuthModal={() => setIsAuthModalOpen(true)}
            onShowToast={showToast}
          />
        )}

        {currentTab === 'member' && currentUser && (
          <MemberPanel
            currentLang={currentLang}
            user={currentUser}
            donations={donations}
            events={events}
            userPurchases={userPurchases}
            onLogout={() => {
              setCurrentUser(null);
              setCurrentTab('home');
            }}
            onShowToast={showToast}
          />
        )}

        {currentTab === 'admin' && currentAdminUser && (
          <AdminPanel
            currentLang={currentLang}
            news={news}
            setNews={setNews}
            events={events}
            setEvents={setEvents}
            activities={activities}
            setActivities={setActivities}
            cultureItems={cultureItems}
            setCultureItems={setCultureItems}
            members={members}
            setMembers={setMembers}
            donations={donations}
            setDonations={setDonations}
            gallery={gallery}
            setGallery={setGallery}
            documents={documents}
            setDocuments={setDocuments}
            settings={settings}
            setSettings={setSettings}
            ads={ads}
            setAds={setAds}
            eventTickets={eventTickets}
            setEventTickets={setEventTickets}
            userPurchases={userPurchases}
            setUserPurchases={setUserPurchases}
            adminAccounts={adminAccounts}
            setAdminAccounts={setAdminAccounts}
            currentAdminUser={currentAdminUser}
            onShowToast={showToast}
            onLogout={async () => {
              try {
                const { logoutAdminApi } = await import('./services/api');
                await logoutAdminApi();
              } catch {}
              setCurrentAdminUser(null);
              setCurrentTab('home');
            }}
          />
        )}
      </main>
      </div>

      {/* 3. FOOTER (Only shown on inner pages, hidden on home view) */}
      {currentTab !== 'home' && (
        <footer className="glass-card border-t border-[#38BDF8]/30 bg-[#0B1426]/95 backdrop-blur-xl text-xs text-gray-300 py-12 px-4 sm:px-6 lg:px-8 mt-auto">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Col 1: About */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#132238] border border-[#38BDF8] flex items-center justify-center font-bold text-[#38BDF8] shadow-[0_0_10px_rgba(56,189,248,0.4)]">
                  K
                </div>
                <span className="font-extrabold text-white text-sm font-heading">{settings.siteName}</span>
              </div>
              <p className="text-gray-300 leading-relaxed text-[11px]">
                {t.footerAboutDesc}
              </p>
              <div className="flex items-center gap-3 pt-2">
                <a href={settings.socialLinks?.facebook || settings.social?.facebook || '#'} target="_blank" rel="noopener noreferrer" className="p-2 rounded-xl bg-[#132238] border border-[#38BDF8]/30 hover:border-[#38BDF8] hover:text-[#38BDF8] hover:shadow-[0_0_10px_rgba(56,189,248,0.5)] transition-all">
                  Facebook
                </a>
                <a href={settings.socialLinks?.instagram || settings.social?.instagram || '#'} target="_blank" rel="noopener noreferrer" className="p-2 rounded-xl bg-[#132238] border border-[#38BDF8]/30 hover:border-[#38BDF8] hover:text-[#38BDF8] hover:shadow-[0_0_10px_rgba(56,189,248,0.5)] transition-all">
                  Instagram
                </a>
                <a href={settings.socialLinks?.youtube || settings.social?.youtube || '#'} target="_blank" rel="noopener noreferrer" className="p-2 rounded-xl bg-[#132238] border border-[#38BDF8]/30 hover:border-[#38BDF8] hover:text-[#38BDF8] hover:shadow-[0_0_10px_rgba(56,189,248,0.5)] transition-all">
                  YouTube
                </a>
              </div>
            </div>

            {/* Col 2: Quick Links */}
            <div className="space-y-2">
              <h4 className="font-bold text-[#7DD3FC] uppercase text-xs tracking-wider">{t.quickAccess || 'Hızlı Erişim'}</h4>
              <ul className="space-y-1.5 text-gray-300">
                <li><button onClick={() => handleNavClick('about')} className="hover:text-[#38BDF8] transition-colors">{t.aboutUs}</button></li>
                <li><button onClick={() => handleNavClick('activities')} className="hover:text-[#38BDF8] transition-colors">{t.activities}</button></li>
                <li><button onClick={() => handleNavClick('news')} className="hover:text-[#38BDF8] transition-colors">{t.news}</button></li>
                <li><button onClick={() => handleNavClick('events')} className="hover:text-[#38BDF8] transition-colors">{t.events}</button></li>
                <li><button onClick={() => handleNavClick('culture')} className="hover:text-[#38BDF8] transition-colors">{t.kurdishCulture}</button></li>
              </ul>
            </div>

            {/* Col 3: Corporate */}
            <div className="space-y-2">
              <h4 className="font-bold text-[#7DD3FC] uppercase text-xs tracking-wider">{t.corporate || 'Kurumsal'}</h4>
              <ul className="space-y-1.5 text-gray-300">
                <li><button onClick={() => handleNavClick('documents')} className="hover:text-[#38BDF8] transition-colors">{t.statuteAndBalance || t.documents}</button></li>
                <li><button onClick={() => handleNavClick('donate')} className="hover:text-[#38BDF8] transition-colors">{t.donate}</button></li>
                <li><button onClick={() => handleNavClick('contact')} className="hover:text-[#38BDF8] transition-colors">{t.contact}</button></li>
                <li><button onClick={() => handleNavClick('admin')} className="text-[#38BDF8] font-semibold hover:underline">{t.adminPanel}</button></li>
              </ul>
            </div>

            {/* Col 4: Contact & Legal */}
            <div className="space-y-2">
              <h4 className="font-bold text-[#7DD3FC] uppercase text-xs tracking-wider">{t.contact || 'İletişim'}</h4>
              <p className="text-gray-300">{settings.address}</p>
              <p className="text-gray-300">📞 {settings.phone}</p>
              <p className="text-[#38BDF8]">✉️ {settings.email}</p>
            </div>
          </div>

          <div className="max-w-7xl mx-auto border-t border-white/5 mt-8 pt-6 flex flex-col sm:flex-row items-center justify-between text-[11px] text-gray-400 gap-4">
            <p>© 2026 {settings.siteName || 'Komeliya Çandî ya Fransî-Kurdî ya Yvelines'}. {t.allRightsReserved || 'Tüm hakları saklıdır.'}</p>
            <div className="flex items-center gap-4">
              <button onClick={() => handleNavClick('documents')} className="hover:underline">{t.privacyPolicy}</button>
              <span>•</span>
              <button onClick={() => handleNavClick('documents')} className="hover:underline">{t.cookiePolicy}</button>
              <span>•</span>
              <button onClick={() => handleNavClick('documents')} className="hover:underline">{t.gdprNotice}</button>
            </div>
          </div>
        </footer>
      )}

      {/* 4. FIXED BOTTOM MOBILE NAVIGATION */}
      <BottomMobileNav
        currentTab={currentTab}
        onNavClick={handleNavClick}
        currentLang={currentLang}
      />

      {/* 5. SLIDE-OVER RIGHT DRAWER */}
      <RightDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        currentLang={currentLang}
        onLanguageChange={setCurrentLang}
        currentTab={currentTab}
        activeTab={currentTab}
        onNavClick={handleNavClick}
        isAdminMode={currentTab === 'admin'}
        onToggleAdmin={() => handleNavClick(currentTab === 'admin' ? 'home' : 'admin')}
        isLoggedIn={!!currentUser}
        onOpenAuth={() => setIsAuthModalOpen(true)}
        onOpenAuthModal={() => setIsAuthModalOpen(true)}
        onLogout={() => setCurrentUser(null)}
      />

      {/* 6. GLOBAL SEARCH MODAL */}
      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        currentLang={currentLang}
        news={news}
        events={events}
        activities={activities}
        cultureItems={cultureItems}
        documents={documents}
        onSelectNews={handleSelectNews}
        onSelectEvent={handleSelectEvent}
        onSelectResult={(type, id) => {
          if (type === 'news') {
            const found = news.find((n) => n.id === id);
            if (found) handleSelectNews(found);
          } else if (type === 'events') {
            const found = events.find((e) => e.id === id);
            if (found) handleSelectEvent(found);
          } else if (type === 'activities') {
            handleNavClick('activities');
          } else if (type === 'culture') {
            handleNavClick('culture');
          } else if (type === 'documents') {
            handleNavClick('documents');
          }
        }}
      />

      {/* 7. AUTH LOGIN / REGISTER MODAL */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        currentLang={currentLang}
        onLoginSuccess={handleLoginSuccess}
      />

      {/* 8. HIGH SECURITY ADMIN AUTHENTICATION MODAL */}
      <AdminAuthModal
        isOpen={isAdminAuthModalOpen}
        onClose={() => setIsAdminAuthModalOpen(false)}
        currentLang={currentLang}
        adminAccounts={adminAccounts}
        siteName={settings.siteName}
        onAdminAuthenticated={(adminAcc) => {
          setCurrentAdminUser(adminAcc);
          setIsAdminAuthModalOpen(false);
          setCurrentTab('admin');
        }}
      />
    </div>
  );
}
